import apiClient from 'apiClient/index';
import { TSupportedCountryCodes, US_COUNTRY_CODE } from 'constants/LCID';
import { shouldAskForLocationUpdate } from 'helpers/shouldAskForLocationUpdate';
import { shouldAskForPetDNADisplayNameUpdate } from 'helpers/shouldAskForPetDNADisplayNameUpdate';
import { DateTime } from 'luxon';
import {
  configure, makeAutoObservable, runInAction,
  toJS,
} from 'mobx';
import { Entitlement } from 'stores/types/Hygraph';

import { BreedResults } from './types/Breeds';
import {
  EPopupSettingType,
  IPopup,
  IPopupList,
  IPopupPetSetting,
  IPopupsStorage,
  IPopupStorage,
  IPopupUserSetting,
  TPopupContent,
  TPopupSetting,
} from './types/Popups';
import { Profile, UserProfile } from './types/Profiles';

export const hasShowDate = (
  popup: IPopup<TPopupSetting>,
): popup is IPopup<IPopupPetSetting | IPopupUserSetting> => typeof popup?.setting === 'object' && 'showDate' in popup.setting;

const isPopupViewed = (
  popupId: string,
  popup: IPopup<TPopupSetting>,
  popupStorage: IPopupStorage[],
): boolean => {
  if (!hasShowDate(popup)) return false;

  const popupInLocalStorage = popupStorage.find((p) => p.id === popupId);
  if (!popupInLocalStorage) return false;

  const settingShowDate = Date.parse(popup.setting.showDate);

  return settingShowDate <= popupInLocalStorage.showDate;
};

configure({
  enforceActions: 'observed',
  isolateGlobalState: true,
  reactionRequiresObservable: true,
});
class PopupsStore {
  petProfile: Profile | null = null;
  userProfile: UserProfile | null = null;
  breedResults: BreedResults | null = null;
  entitlements: Set<Entitlement> | null = null;
  popupsStorage: IPopupsStorage | null = null;
  countryCode: TSupportedCountryCodes | null = null;
  isAnySurveyFilled: boolean = false;

  popupList: IPopupList | null = null;
  popups: IPopup<TPopupSetting, TPopupContent>[] = [];
  filteredPopups: IPopup<TPopupSetting, TPopupContent>[] = [];

  isPopupListLoading: boolean = false;
  isPopupsLoading: boolean = false;
  isUserSpecificPopupsLoading: boolean = false;
  isPetProfileLoading: boolean = false;
  isUserProfileLoading: boolean = false;
  isSurveySessionLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  getPopupList = async (id: string) => {
    this.isPopupListLoading = true;
    const popupList = await apiClient.popups.getPopupList(id);
    runInAction(() => {
      this.popupList = popupList.data.data.popupList;
      this.isPopupListLoading = false;
      this.setAvailablePopups();
    });
  };

  getPopups = async () => {
    this.popups = [];

    this.isPopupsLoading = true;

    const showDateLte = DateTime.now().toISODate();
    const filters = {
      active: true,
      content: { PopupSimpleContent: {} },
      OR: [
        { setting: { PopupUserSetting: { showDate_lte: showDateLte } } },
        { setting: { PopupPetSetting: { showDate_lte: showDateLte } } },
      ],
    };

    const popups = await apiClient.popups.getPopups({ where: filters });

    runInAction(() => {
      this.popups = popups.data.data.popups;
      this.isPopupsLoading = false;
      this.setAvailablePopups();
    });
  };

  setProfile = (profile: Profile | null, breedResults: BreedResults | null) => {
    this.petProfile = profile;
    this.breedResults = breedResults;
    this.setAvailablePopups();
  };

  setPopupsStorage = (popupsLocalStorage: IPopupsStorage) => {
    this.popupsStorage = popupsLocalStorage;
    this.setAvailablePopups();
  };

  setEntitlements = (entitlements: Set<Entitlement> | null) => {
    this.entitlements = entitlements;
    this.setAvailablePopups();
  };

  setCountryCode = (countryCode: TSupportedCountryCodes) => {
    this.countryCode = countryCode;
    this.setAvailablePopups();
  };

  setIsAnySurveyFilled = (isAnySurveyFilled: boolean) => {
    this.isAnySurveyFilled = isAnySurveyFilled;
  };

  setIsPetProfileLoading = (isPetProfileLoading: boolean) => {
    this.isPetProfileLoading = isPetProfileLoading;
    this.setAvailablePopups();
  };

  setIsSurveySessionLoading = (isSurveySessionLoading: boolean) => {
    this.isSurveySessionLoading = isSurveySessionLoading;
    this.setAvailablePopups();
  };

  setIsUserProfileLoading = (isUserProfileLoading: boolean) => {
    this.isUserProfileLoading = isUserProfileLoading;
    this.setAvailablePopups();
  };

  setUserProfile = (userProfile: UserProfile | null) => {
    this.userProfile = userProfile;
    this.setAvailablePopups();
  };

  setAvailablePopups() {
    if (this.isPopupsLoading) {
      this.filteredPopups = [];
    }

    this.filteredPopups = this.popups.filter(({ setting }) => {
      // eslint-disable-next-line no-underscore-dangle
      if (setting.__typename === EPopupSettingType.PopupPetSetting) {
        return this.isPetPopupEligible(setting as IPopupPetSetting);
      }
      // eslint-disable-next-line no-underscore-dangle
      if (setting.__typename === EPopupSettingType.PopupUserSetting) {
        return this.isUserPopupEligible(setting as IPopupUserSetting);
      }
      return false;
    });
  }

  isUserPopupEligible = (setting: IPopupUserSetting) => {
    let valid = true;

    if (setting.noSurveyFilled && this.isAnySurveyFilled) {
      valid = false;
    }

    if (
      setting.noDisplayedUsername
      && !shouldAskForPetDNADisplayNameUpdate(this.userProfile)
    ) {
      valid = false;
    }

    return valid;
  };

  isPetPopupEligible = (setting: IPopupPetSetting) => {
    let birthdayString: string | undefined;

    if (this.petProfile?.birthDate) {
      birthdayString = this.petProfile.birthDate;
    } else if (this.petProfile?.birthYearEstimate) {
      birthdayString = `${this.petProfile.birthYearEstimate}-01-01`;
    }

    const birthday = birthdayString ? DateTime.fromISO(birthdayString) : DateTime.now();
    const now = DateTime.now();

    const petAge = Math.floor(now.diff(birthday, 'years').years);

    let valid = true;

    if (setting.petAgeFrom !== null && petAge < setting.petAgeFrom) {
      valid = false;
    }

    if (setting.petAgeTo !== null && petAge > setting.petAgeTo) {
      valid = false;
    }

    if (
      setting.entitlements.length
        && !setting.entitlements.every((e) => this.entitlements?.has(e))
    ) {
      valid = false;
    }

    if (
      setting.healthUpgrade
        && (!this.entitlements?.has(Entitlement.TEST_ANALYSIS_COMPLETE)
          || this.entitlements?.has(Entitlement.HEALTH) || this.countryCode !== US_COUNTRY_CODE)
    ) {
      valid = false;
    }

    const twoWeeksAgo = now.minus({ days: 14 });
    const birthDateThisYear = birthday.set({ year: now.year });

    if (
      setting.birthdayRelated
        && !(birthDateThisYear >= twoWeeksAgo && twoWeeksAgo <= now)
    ) {
      valid = false;
    }

    if (setting.breeds.length) {
      const breedIDs = setting.breeds.map((b) => b.breedID);
      const breedResultsIDs = this.breedResults?.breeds.map((b) => b.id) ?? [];
      if (!breedResultsIDs.some((b) => breedIDs.includes(b))) {
        valid = false;
      }
    }

    if (setting.noLocation && this.petProfile && !shouldAskForLocationUpdate(this.petProfile)) {
      valid = false;
    }

    return valid;
  };

  get isLoading() {
    return (
      this.isPopupListLoading
      || this.isPopupsLoading
      || this.isPetProfileLoading
      || this.isUserProfileLoading
      || this.isSurveySessionLoading
    );
  }

  get currentPopup(): IPopup<TPopupSetting, TPopupContent> | null {
    if (
      this.isLoading
      || this.popupList === null
      || this.petProfile === null
      || this.popupsStorage === null
    ) return null;

    const popupsOrder = this.popupList.popups;
    const userPopupsLocalStorage = this.popupsStorage.popups?.filter(
      (p) => p.userId && !p.testGuid,
    ) ?? [];
    const testPopupsLocalStorage = this.popupsStorage.popups?.filter(
      (p) => p.testGuid === this.petProfile?.testGUID,
    ) ?? [];

    let popup: IPopup<TPopupSetting, TPopupContent> | null = null;

    // eslint-disable-next-line no-restricted-syntax
    for (const popupInOrder of popupsOrder) {
      popup = this.filteredPopups.find(
        (p) => p.id === popupInOrder.id,
      ) ?? null;

      // eslint-disable-next-line no-underscore-dangle
      if (popup?.setting.__typename === EPopupSettingType.PopupUserSetting) {
        if (isPopupViewed(popup.id, popup, userPopupsLocalStorage)) {
          popup = null;
        }
      }

      // eslint-disable-next-line no-underscore-dangle
      if (popup?.setting.__typename === EPopupSettingType.PopupPetSetting) {
        if (isPopupViewed(popup.id, popup, testPopupsLocalStorage)) {
          popup = null;
        }
      }

      if (popup) break;
    }

    return popup;
  }
}

export default PopupsStore;
